/**
 * /app/search  —  Change 7: Explore & Plan route
 * Two modes via a top segmented control:
 *   Explore   — place search + pulsing radius + risk overlay + summary sheet
 *   Plan route — from/to fields + Mapbox Directions + risky segment overlay
 */
import { createFileRoute } from "@tanstack/react-router";
import { useCallback, useEffect, useRef, useState } from "react";
import Map, { Layer, Marker, NavigationControl, Source } from "react-map-gl/mapbox";
import type { MapRef } from "react-map-gl/mapbox";
import "mapbox-gl/dist/mapbox-gl.css";
import {
  Bike,
  Car,
  Footprints,
  Loader2,
  MapPin,
  Navigation,
  X,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { SeverityPill } from "@/components/app/severity-pill";
import { WeatherIcon } from "@/components/app/WeatherIcon";
import { liveApi } from "@/lib/api/live";
import {
  locationApi,
  routeApi,
  weatherApi,
  type ProcessedRoute,
} from "@/lib/api";
import { usePlatformConfig } from "@/lib/config";
import { reverseGeocodeMapbox, searchPlacesMapbox, type MapboxSearchHit } from "@/lib/geo";
import { cn } from "@/lib/utils";

export const Route = createFileRoute("/app/search")({
  head: () => ({ meta: [{ title: "Search" }] }),
  component: SearchPage,
});

// ── helpers ──────────────────────────────────────────────────────────────────
function fmtDistance(m: number) {
  return m >= 1000 ? `${(m / 1000).toFixed(1)} km` : `${Math.round(m)} m`;
}
function fmtDuration(s: number) {
  const m = Math.round(s / 60);
  return m < 60 ? `${m} min` : `${Math.floor(m / 60)}h ${m % 60}min`;
}

/** GeoJSON circle polygon (64-point approximation). */
function circleGeoJSON(lat: number, lng: number, radiusKm: number): GeoJSON.Feature {
  const steps = 64;
  const coords: [number, number][] = [];
  for (let i = 0; i <= steps; i++) {
    const angle = (i / steps) * 2 * Math.PI;
    const dLat = (radiusKm / 111) * Math.cos(angle);
    const dLng = (radiusKm / (111 * Math.cos((lat * Math.PI) / 180))) * Math.sin(angle);
    coords.push([lng + dLng, lat + dLat]);
  }
  return { type: "Feature", properties: {}, geometry: { type: "Polygon", coordinates: [coords] } };
}

const SEV_COLOR: Record<string, string> = {
  LOW: "#22c55e", WATCH: "#eab308", WARNING: "#f97316", SEVERE: "#ef4444",
};

// ── PlaceSearch component ─────────────────────────────────────────────────────
function PlaceSearch({
  placeholder,
  value,
  onSelect,
  onClear,
  token,
  className,
}: {
  placeholder: string;
  value: string;
  onSelect: (hit: MapboxSearchHit) => void;
  onClear: () => void;
  token: string;
  className?: string;
}) {
  const [query, setQuery] = useState(value);
  const [results, setResults] = useState<MapboxSearchHit[]>([]);
  const [open, setOpen] = useState(false);
  const [searching, setSearching] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);
  const reqId = useRef(0);

  useEffect(() => { setQuery(value); }, [value]);

  useEffect(() => {
    if (!open) return;
    function handler(e: MouseEvent) {
      if (!containerRef.current?.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, [open]);

  function handleChange(e: React.ChangeEvent<HTMLInputElement>) {
    const q = e.target.value;
    setQuery(q);
    if (timerRef.current) clearTimeout(timerRef.current);
    abortRef.current?.abort();
    if (q.trim().length < 2) {
      setResults([]);
      setSearching(false);
      setOpen(false);
      return;
    }
    setSearching(true);
    setOpen(true);
    const id = ++reqId.current;
    timerRef.current = setTimeout(async () => {
      const ctrl = new AbortController();
      abortRef.current = ctrl;
      try {
        const hits = await searchPlacesMapbox(q, token, 8, ctrl.signal);
        if (ctrl.signal.aborted || id !== reqId.current) return;
        setResults(hits);
        setOpen(true);
      } catch {
        if (ctrl.signal.aborted || id !== reqId.current) return;
        setResults([]);
      } finally {
        if (id === reqId.current) setSearching(false);
      }
    }, 200);
  }

  function handleSelect(hit: MapboxSearchHit) {
    setQuery(hit.placeName);
    setResults([]);
    setOpen(false);
    onSelect(hit);
  }

  return (
    <div ref={containerRef} className={cn("relative", className)}>
      <div className="relative">
        <input
          type="text"
          value={query}
          onChange={handleChange}
          onFocus={() => results.length > 0 && setOpen(true)}
          placeholder={placeholder}
          className="h-10 w-full rounded-md border border-input bg-background px-3 pr-8 text-sm focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        />
        {searching && (
          <Loader2 className="absolute right-8 top-1/2 h-3.5 w-3.5 -translate-y-1/2 animate-spin text-muted-foreground" />
        )}
        {query && (
          <button
            type="button"
            onClick={() => {
              abortRef.current?.abort();
              if (timerRef.current) clearTimeout(timerRef.current);
              setQuery("");
              setResults([]);
              setSearching(false);
              setOpen(false);
              onClear();
            }}
            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            aria-label="Clear"
          >
            <X className="h-3.5 w-3.5" />
          </button>
        )}
      </div>
      {open && (
        <div className="absolute z-50 mt-1 max-h-56 w-full overflow-y-auto rounded-md border border-border bg-popover shadow-lg">
          {searching && results.length === 0 && (
            <div className="flex items-center gap-2 px-3 py-2 text-sm text-muted-foreground">
              <Loader2 className="h-3.5 w-3.5 animate-spin" />
              Searching…
            </div>
          )}
          {results.map((hit) => (
            <button
              key={hit.id}
              type="button"
              onMouseDown={(e) => { e.preventDefault(); handleSelect(hit); }}
              className="flex w-full items-start gap-2 px-3 py-2 text-left text-sm hover:bg-accent"
            >
              <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0 text-muted-foreground" />
              <span className="line-clamp-2">{hit.placeName}</span>
            </button>
          ))}
          {!searching && results.length === 0 && (
            <p className="px-3 py-2 text-sm text-muted-foreground">No places match “{query.trim()}”.</p>
          )}
        </div>
      )}
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────
type Mode = "explore" | "route";
type TravelMode = "driving" | "motorbike" | "walking";

export default function SearchPage() {
  const { config } = usePlatformConfig();
  const token = config.mapbox.accessToken ?? "";
  const mapRef = useRef<MapRef>(null);

  const [mode, setMode] = useState<Mode>("explore");

  // ── Explore state ───────────────────────────────────────────────────────────
  const [explorePlace, setExplorePlace] = useState<MapboxSearchHit | null>(null);
  const [exploreRadius, setExploreRadius] = useState(2);
  const [exploreCenter, setExploreCenter] = useState<{ lat: number; lng: number } | null>(null);
  const [explorePulse, setExplorePulse] = useState(false);
  const [explorePlaceLabel, setExplorePlaceLabel] = useState("");
  const [exploreWeather, setExploreWeather] = useState<{
    temperature: number;
    conditionText: string;
    conditionIcon: string;
    humidity: number;
    windKph: number;
    rainChancePct: number;
  } | null>(null);
  const [weatherLoading, setWeatherLoading] = useState(false);
  const [exploreRisk, setExploreRisk] = useState<{
    severity: string;
    advice: string;
    rain24hMm?: number | null;
    riverDischargeRatio?: number | null;
  } | null>(null);
  const [riskLoading, setRiskLoading] = useState(false);

  function handleExploreSelect(hit: MapboxSearchHit) {
    setExplorePlace(hit);
    setExploreCenter({ lat: hit.latitude, lng: hit.longitude });
    setExplorePlaceLabel(hit.placeName);
    setExploreWeather(null);
    setExploreRisk(null);
    mapRef.current?.flyTo({
      center: [hit.longitude, hit.latitude],
      zoom: 13,
      duration: 1800,
      essential: true,
    });
    // Trigger pulse animation after fly
    setTimeout(() => { setExplorePulse(false); setTimeout(() => setExplorePulse(true), 50); }, 400);
    // Fetch weather for this location
    setWeatherLoading(true);
    weatherApi.current({ lat: hit.latitude, lng: hit.longitude })
      .then((w) => setExploreWeather(w))
      .catch(() => setExploreWeather(null))
      .finally(() => setWeatherLoading(false));
    setRiskLoading(true);
    liveApi.riskAt({
      latitude: hit.latitude,
      longitude: hit.longitude,
      county: hit.county,
      ward: hit.ward,
      label: hit.placeName.split(",")[0],
    })
      .then((risk) => setExploreRisk(risk))
      .catch(() => setExploreRisk(null))
      .finally(() => setRiskLoading(false));
  }

  async function saveExplorePlace() {
    if (!explorePlace) return;
    try {
      await locationApi.savePlace({
        label: explorePlace.placeName.split(",")[0] ?? explorePlace.placeName,
        county: explorePlace.county ?? "",
        ward: explorePlace.ward ?? "",
        latitude: explorePlace.latitude,
        longitude: explorePlace.longitude,
      });
      alert("Place saved!");
    } catch {
      alert("Could not save place.");
    }
  }

  // Circle GeoJSON for explore radius
  const circleFeature: GeoJSON.FeatureCollection | null = exploreCenter
    ? { type: "FeatureCollection", features: [circleGeoJSON(exploreCenter.lat, exploreCenter.lng, exploreRadius)] }
    : null;

  // ── Route planner state ────────────────────────────────────────────────────
  const [fromLabel, setFromLabel] = useState("");
  const [fromCoords, setFromCoords] = useState<[number, number] | null>(null);
  const [toLabel, setToLabel] = useState("");
  const [toCoords, setToCoords] = useState<[number, number] | null>(null);
  const [travelMode, setTravelMode] = useState<TravelMode>("driving");
  const [computing, setComputing] = useState(false);
  const [routeResult, setRouteResult] = useState<{
    routes: ProcessedRoute[];
    allDangerous: boolean;
    hasAlternative: boolean;
    safestIdx: number;
  } | null>(null);
  const [selectedRouteIdx, setSelectedRouteIdx] = useState(0);
  const [routeError, setRouteError] = useState<string | null>(null);
  const [gpsLoading, setGpsLoading] = useState(false);

  async function useMyLocation() {
    if (!navigator.geolocation) return;
    setGpsLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (pos) => {
        const { latitude, longitude } = pos.coords;
        setFromCoords([longitude, latitude]);
        const rev = token ? await reverseGeocodeMapbox(latitude, longitude, token).catch(() => null) : null;
        setFromLabel(rev?.placeName ?? `${latitude.toFixed(4)}, ${longitude.toFixed(4)}`);
        setGpsLoading(false);
      },
      () => setGpsLoading(false),
      { timeout: 8000 },
    );
  }

  function swapFromTo() {
    setFromLabel(toLabel); setFromCoords(toCoords);
    setToLabel(fromLabel); setToCoords(fromCoords);
    setRouteResult(null); setRouteError(null);
  }

  const canRoute = Boolean(fromCoords && toCoords);

  async function computeRoute() {
    if (!fromCoords || !toCoords) return;
    setComputing(true); setRouteResult(null); setRouteError(null);
    try {
      const profile = travelMode === "walking" ? "walking" : "driving";
      const res = await routeApi.risks({ from: fromCoords, to: toCoords, profile });
      setRouteResult({
        routes: res.routes,
        allDangerous: res.allRoutesDangerous,
        hasAlternative: res.hasAlternative,
        safestIdx: res.safestRouteIndex,
      });
      setSelectedRouteIdx(res.safestRouteIndex);
      // Fit map to route bounds
      if (res.routes[res.safestRouteIndex]?.geometry?.coordinates?.length) {
        const coords = res.routes[res.safestRouteIndex].geometry.coordinates;
        const lngs = coords.map((c) => c[0]);
        const lats = coords.map((c) => c[1]);
        mapRef.current?.fitBounds(
          [[Math.min(...lngs), Math.min(...lats)], [Math.max(...lngs), Math.max(...lats)]],
          { padding: 48, duration: 1200 },
        );
      }
    } catch {
      setRouteError("Could not compute route. Please try again.");
    } finally {
      setComputing(false);
    }
  }

  const selectedRoute = routeResult?.routes.find((r) => r.index === selectedRouteIdx) ?? routeResult?.routes[0];

  // Build GeoJSON for route lines
  const safeRouteGeoJSON: GeoJSON.FeatureCollection | null =
    selectedRoute
      ? {
          type: "FeatureCollection",
          features: [
            {
              type: "Feature",
              properties: { safe: true },
              geometry: { type: "LineString", coordinates: selectedRoute.geometry.coordinates },
            },
          ],
        }
      : null;

  // Collect risky segment lines as a separate layer
  const riskyGeoJSON: GeoJSON.FeatureCollection | null =
    selectedRoute?.riskySegments.length
      ? {
          type: "FeatureCollection",
          features: selectedRoute.riskySegments.map((seg, i) => ({
            type: "Feature" as const,
            properties: { severity: seg.severity, label: seg.label, index: i },
            geometry: { type: "LineString" as const, coordinates: seg.coordinates },
          })),
        }
      : null;

  // Default map center — Nairobi
  const defaultCenter = config.mapbox.defaultCenter ?? { lat: -1.286, lng: 36.817 };

  if (!token) {
    return (
      <div className="space-y-3">
        <h2 className="font-display text-lg font-bold">Search</h2>
        <p className="rounded-md bg-muted px-4 py-3 text-sm text-muted-foreground">
          Map search is not configured. Set <code>VITE_MAPBOX_PUBLIC_TOKEN</code> to enable it.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <h2 className="font-display text-lg font-bold">Search</h2>

      {/* Segmented control */}
      <div className="flex gap-1 rounded-lg border border-border bg-muted p-1">
        {(["explore", "route"] as Mode[]).map((m) => (
          <button
            key={m}
            type="button"
            onClick={() => setMode(m)}
            className={cn(
              "flex-1 rounded-md py-2 text-sm font-semibold transition-colors",
              mode === m ? "bg-background text-foreground shadow-sm" : "text-muted-foreground hover:text-foreground",
            )}
          >
            {m === "explore" ? "Explore" : "Plan route"}
          </button>
        ))}
      </div>

      {/* ── EXPLORE MODE ─────────────────────────────────────────────────── */}
      {mode === "explore" && (
        <div className="space-y-3">
          <PlaceSearch
            placeholder="Search a place in Kenya…"
            value={explorePlaceLabel}
            onSelect={handleExploreSelect}
            onClear={() => { setExplorePlace(null); setExploreCenter(null); setExplorePlaceLabel(""); setExplorePulse(false); setExploreWeather(null); setExploreRisk(null); }}
            token={token}
          />

          {/* Radius chips */}
          {exploreCenter && (
            <div className="flex items-center gap-2">
              <span className="text-xs font-semibold text-muted-foreground">Radius:</span>
              {[1, 2, 5, 10].map((r) => (
                <button
                  key={r}
                  type="button"
                  onClick={() => setExploreRadius(r)}
                  className={cn(
                    "rounded-full px-3 py-1 text-xs font-semibold border transition-colors",
                    exploreRadius === r
                      ? "bg-primary text-primary-foreground border-primary"
                      : "border-border text-muted-foreground hover:border-primary/50",
                  )}
                >
                  {r} km
                </button>
              ))}
            </div>
          )}

          {/* Map */}
          <div className="overflow-hidden rounded-xl border border-border" style={{ height: 340 }}>
            <Map
              ref={mapRef}
              mapboxAccessToken={token}
              mapStyle={config.mapbox.styleUrl}
              initialViewState={{
                longitude: exploreCenter?.lng ?? defaultCenter.lng,
                latitude: exploreCenter?.lat ?? defaultCenter.lat,
                zoom: exploreCenter ? 13 : config.mapbox.defaultZoom ?? 7,
              }}
              style={{ width: "100%", height: "100%" }}
            >
              <NavigationControl position="bottom-right" />

              {/* Pulsing radius circle */}
              {circleFeature && (
                <>
                  <Source id="explore-circle" type="geojson" data={circleFeature}>
                    {/* Blue safe fill */}
                    <Layer
                      id="explore-circle-fill"
                      type="fill"
                      paint={{ "fill-color": "#3b82f6", "fill-opacity": explorePulse ? 0.15 : 0.08 }}
                    />
                    {/* Blue border */}
                    <Layer
                      id="explore-circle-border"
                      type="line"
                      paint={{ "line-color": "#3b82f6", "line-width": 2, "line-dasharray": [3, 2] }}
                    />
                  </Source>
                </>
              )}

              {/* Centre marker */}
              {exploreCenter && (
                <Marker longitude={exploreCenter.lng} latitude={exploreCenter.lat} anchor="bottom">
                  <div className="flex flex-col items-center">
                    <div className="rounded-md bg-[#064E3B] px-2 py-0.5 text-[10px] font-bold text-white shadow">
                      {(explorePlace?.placeName ?? "").split(",")[0]}
                    </div>
                    <MapPin className="h-5 w-5 text-[#064E3B]" />
                  </div>
                </Marker>
              )}
            </Map>
          </div>

          {/* Summary sheet */}
          {explorePlace && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0">
                  <h3 className="font-display text-base font-bold">
                    {explorePlace.placeName.split(",")[0]}
                  </h3>
                  <p className="text-xs text-muted-foreground">
                    {[explorePlace.ward, explorePlace.county].filter(Boolean).join(", ")}
                  </p>
                </div>
                {/* Weather widget */}
                <div className="shrink-0">
                  {weatherLoading ? (
                    <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                      <Loader2 className="h-4 w-4 animate-spin" />
                      <span>Weather…</span>
                    </div>
                  ) : exploreWeather ? (
                    <div className="flex items-center gap-2 rounded-lg border border-border bg-muted/40 px-3 py-2">
                      <WeatherIcon icon={exploreWeather.conditionIcon} size={36} />
                      <div className="text-right">
                        <p className="text-xl font-bold leading-none">{Number(exploreWeather.temperature)}°C</p>
                        <p className="mt-0.5 text-[10px] text-muted-foreground">{String(exploreWeather.conditionText)}</p>
                        <p className="text-[10px] text-muted-foreground">🌧 {Number(exploreWeather.rainChancePct)}%</p>
                      </div>
                    </div>
                  ) : null}
                </div>
              </div>

              {exploreWeather && (
                <div className="flex gap-4 text-xs text-muted-foreground border-t border-border pt-2">
                  <span>💧 {Number(exploreWeather.humidity)}% humidity</span>
                  <span>🌬️ {Number(exploreWeather.windKph)} km/h wind</span>
                </div>
              )}

              {riskLoading ? (
                <p className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Checking flood risk…
                </p>
              ) : exploreRisk ? (
                <div className="space-y-1">
                  <div className="flex items-center gap-2">
                    <SeverityPill severity={exploreRisk.severity} />
                    <span className="text-sm font-semibold">Flood risk here</span>
                  </div>
                  <p className="text-sm text-muted-foreground">{exploreRisk.advice}</p>
                  {Number(exploreRisk.riverDischargeRatio) >= 1.1 ? (
                    <p className="text-xs text-muted-foreground">
                      River is {Number(exploreRisk.riverDischargeRatio).toFixed(1)}× its recent level.
                    </p>
                  ) : null}
                </div>
              ) : null}

              <p className="text-sm text-muted-foreground">
                The blue circle is the area you are looking at. Flood risk above is for this place.
              </p>
            </div>
          )}
        </div>
      )}

      {/* ── ROUTE MODE ───────────────────────────────────────────────────── */}
      {mode === "route" && (
        <div className="space-y-3">
          {/* From / To — stacked vertically with swap between */}
          <div className="space-y-1">
            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">From</label>
              <div className="flex gap-2">
                <PlaceSearch
                  placeholder="Starting point…"
                  value={fromLabel}
                  onSelect={(h) => { setFromCoords([h.longitude, h.latitude]); setFromLabel(h.placeName); setRouteResult(null); }}
                  onClear={() => { setFromCoords(null); setFromLabel(""); setRouteResult(null); }}
                  token={token}
                  className="flex-1"
                />
                <button
                  type="button"
                  onClick={() => void useMyLocation()}
                  disabled={gpsLoading}
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-md border border-border hover:bg-muted disabled:opacity-50"
                  aria-label="Use my location"
                >
                  {gpsLoading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Navigation className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Swap button — centred between the two fields */}
            <div className="flex items-center justify-center">
              <button
                type="button"
                onClick={swapFromTo}
                className="flex h-8 w-8 items-center justify-center rounded-full border border-border bg-background shadow-sm hover:bg-muted"
                aria-label="Swap from and to"
                title="Swap start and destination"
              >
                {/* Two stacked MapPin dots with vertical arrows to indicate swap */}
                <svg viewBox="0 0 16 16" className="h-4 w-4 text-muted-foreground fill-none stroke-current stroke-2" strokeLinecap="round" strokeLinejoin="round">
                  {/* Up arrow */}
                  <polyline points="5,10 5,4 3,6" />
                  <polyline points="5,4 7,6" />
                  {/* Down arrow */}
                  <polyline points="11,6 11,12 9,10" />
                  <polyline points="11,12 13,10" />
                </svg>
              </button>
            </div>

            <div>
              <label className="mb-1 block text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">To</label>
              <PlaceSearch
                placeholder="Destination…"
                value={toLabel}
                onSelect={(h) => { setToCoords([h.longitude, h.latitude]); setToLabel(h.placeName); setRouteResult(null); }}
                onClear={() => { setToCoords(null); setToLabel(""); setRouteResult(null); }}
                token={token}
              />
            </div>
          </div>

          {/* Travel mode */}
          <div className="flex gap-2">
            {([
              ["driving", Car, "Drive"],
              ["motorbike", Bike, "Motorbike"],
              ["walking", Footprints, "Walk"],
            ] as [TravelMode, typeof Car, string][]).map(([m, Icon, label]) => (
              <button
                key={m}
                type="button"
                onClick={() => setTravelMode(m)}
                className={cn(
                  "flex flex-1 items-center justify-center gap-1.5 rounded-md border py-2 text-xs font-semibold transition-colors",
                  travelMode === m ? "bg-primary text-primary-foreground border-primary" : "border-border text-muted-foreground hover:border-primary/50",
                )}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>

          {travelMode === "motorbike" && (
            <p className="rounded-md bg-warning-soft px-3 py-2 text-xs text-warning">
              ⚠️ Motorcycles are especially vulnerable to flooded roads. Take extra care at risky segments.
            </p>
          )}

          <Button className="w-full" disabled={!canRoute || computing} onClick={() => void computeRoute()}>
            {computing ? <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Computing…</> : "Find safe route"}
          </Button>

          {routeError && <p className="rounded-md bg-severe-soft px-3 py-2 text-xs text-severe">{routeError}</p>}

          {/* Map — always visible so user can see context */}
          <div className="overflow-hidden rounded-xl border border-border" style={{ height: 340 }}>
            <Map
              ref={mapRef}
              mapboxAccessToken={token}
              mapStyle={config.mapbox.styleUrl}
              initialViewState={{
                longitude: defaultCenter.lng,
                latitude: defaultCenter.lat,
                zoom: config.mapbox.defaultZoom ?? 7,
              }}
              style={{ width: "100%", height: "100%" }}
            >
              <NavigationControl position="bottom-right" />

              {/* Safe route — solid blue */}
              {safeRouteGeoJSON && (
                <Source id="route-safe" type="geojson" data={safeRouteGeoJSON}>
                  <Layer
                    id="route-safe-line"
                    type="line"
                    paint={{ "line-color": "#2563eb", "line-width": 5, "line-cap": "round", "line-join": "round" }}
                  />
                </Source>
              )}

              {/* Risky segments — red dashed overlay */}
              {riskyGeoJSON && (
                <Source id="route-risky" type="geojson" data={riskyGeoJSON}>
                  <Layer
                    id="route-risky-line"
                    type="line"
                    paint={{ "line-color": "#ef4444", "line-width": 6, "line-cap": "round", "line-dasharray": [2, 1.5] }}
                  />
                </Source>
              )}

              {/* From / To markers */}
              {fromCoords && (
                <Marker longitude={fromCoords[0]} latitude={fromCoords[1]} anchor="bottom">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#064E3B] text-white shadow">
                    <span className="text-[9px] font-bold">A</span>
                  </div>
                </Marker>
              )}
              {toCoords && (
                <Marker longitude={toCoords[0]} latitude={toCoords[1]} anchor="bottom">
                  <div className="flex h-6 w-6 items-center justify-center rounded-full bg-[#064E3B] text-white shadow">
                    <span className="text-[9px] font-bold">B</span>
                  </div>
                </Marker>
              )}

              {/* Risky segment markers */}
              {selectedRoute?.riskySegments.map((seg, i) => {
                const mid = seg.coordinates[Math.floor(seg.coordinates.length / 2)];
                if (!mid) return null;
                return (
                  <Marker key={i} longitude={mid[0]} latitude={mid[1]} anchor="bottom">
                    <div
                      className="max-w-[120px] rounded-md px-2 py-1 text-[9px] font-bold text-white shadow"
                      style={{ backgroundColor: SEV_COLOR[seg.severity] ?? "#ef4444" }}
                    >
                      ⚠ {seg.label}
                    </div>
                  </Marker>
                );
              })}
            </Map>
          </div>

          {/* Route summary sheet */}
          {routeResult && selectedRoute && (
            <div className="rounded-xl border border-border bg-card p-4 space-y-3">
              {routeResult.allDangerous && (
                <p className="rounded-md bg-severe-soft px-3 py-2 text-xs font-semibold text-severe">
                  ⚠️ All available routes pass through risk areas. Avoid travel if possible.
                </p>
              )}
              {!routeResult.allDangerous && routeResult.hasAlternative && selectedRoute.riskyCount === 0 && (
                <p className="rounded-md bg-success-soft px-3 py-2 text-xs font-semibold text-success">
                  ✓ Safest route selected — no known hazards on this path.
                </p>
              )}
              {!routeResult.allDangerous && selectedRoute.riskyCount === 0 && (
                <p className="rounded-md bg-success-soft px-3 py-2 text-xs font-semibold text-success">
                  ✓ No known hazards on this route.
                </p>
              )}

              <div className="flex gap-4 text-sm">
                <div>
                  <p className="text-[10px] font-semibold uppercase text-muted-foreground">Distance</p>
                  <p className="font-bold">{fmtDistance(selectedRoute.distance)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase text-muted-foreground">ETA</p>
                  <p className="font-bold">{fmtDuration(selectedRoute.duration)}</p>
                </div>
                <div>
                  <p className="text-[10px] font-semibold uppercase text-muted-foreground">Hazard areas</p>
                  <p className={cn("font-bold", selectedRoute.riskyCount > 0 ? "text-severe" : "text-success")}>
                    {selectedRoute.riskyCount}
                  </p>
                </div>
              </div>

              {/* Alternative routes selector */}
              {routeResult.routes.length > 1 && (
                <div className="space-y-1">
                  <p className="text-[11px] font-semibold uppercase text-muted-foreground">Routes</p>
                  {routeResult.routes.map((r, i) => (
                    <button
                      key={r.index}
                      type="button"
                      onClick={() => setSelectedRouteIdx(r.index)}
                      className={cn(
                        "flex w-full items-center justify-between rounded-md border px-3 py-2 text-sm transition-colors",
                        selectedRouteIdx === r.index ? "border-primary bg-primary-soft text-primary" : "border-border",
                      )}
                    >
                      <span className="font-semibold">
                        {i === 0 ? "Safest route" : `Alternative ${i}`}
                      </span>
                      <span className="text-xs text-muted-foreground">
                        {fmtDistance(r.distance)} · {r.riskyCount} hazard{r.riskyCount !== 1 ? "s" : ""}
                      </span>
                    </button>
                  ))}
                </div>
              )}

              {/* Risky segment list */}
              {selectedRoute.riskySegments.length > 0 && (
                <div className="space-y-1">
                  <p className="text-[11px] font-semibold uppercase text-muted-foreground">Hazard areas on route</p>
                  {selectedRoute.riskySegments.map((seg, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 rounded-md border border-border px-3 py-2"
                    >
                      <span
                        className="h-2.5 w-2.5 shrink-0 rounded-full"
                        style={{ backgroundColor: SEV_COLOR[seg.severity] ?? "#ef4444" }}
                      />
                      <div className="min-w-0">
                        <p className="truncate text-xs font-semibold">{seg.label}</p>
                        <p className="text-[10px] text-muted-foreground">
                          {seg.hazardType.replace(/_/g, " ")} · {seg.severity}
                        </p>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
